import axios from 'axios';
import { shippingApi } from '../../api/shipping';
import {
  GET_SHIPPING_REVIEW_DATA_SUCCESS,
  SET_SHIPPING_ITEM_COUNTS,
  UPDATE_SHIPPING_REVIEW_DATA_SUCCESS,
  GET_ARTA_SUBTYPES_SUCCESS,
  SET_SESSION_TITLE,
  RESET_SHIPPING_REVIEW_DATA,
  IS_SHIPPING_REVIEW_DATA_LOADING
} from './reducer';
import { awsServer } from '../../utilities/UrlPaths';
import { fieldsToCompareForConfidenceLevel } from '../../constants';

export const resetShippingReviewData = () => (dispatch) => {
  dispatch({ type: RESET_SHIPPING_REVIEW_DATA });
};

const isLotManuallyModified = (extractedObjects, savedObjects) => {
  // if there are only saved objects, no extracted, they had to be manually added
  if ((!extractedObjects || extractedObjects?.length === 0) && savedObjects?.length > 0) return true;
  return extractedObjects?.some((item) => {
    const matchingObj = savedObjects?.find((element) => element.id === item.id);
    return Object.keys(item).some((key) => {
      return fieldsToCompareForConfidenceLevel.includes(key) && item[key] !== matchingObj?.[key];
    });
  });
};

/**
 * Get confidence level for an object by comparing argumentObj and extractedObj
 * This data manipulation is necessary because the BE API doesn't use the
 * same field names for confidence score field names as for objects properties and returns more fields than necessary
 * @param {string} id - The object id
 * @param {Object} argumentObj - The argument object from shippingInfo.arguments.objects
 * @param {Object} extractedObj - The extracted object from shippingInfo.objectsExtraction.objects
 * @param {Object} shippingInfo - The shipping review data object
 * @returns {number} Confidence level (0-1)
 */
const confidenceProperties = {
  depth: 'depth',
  width: 'width',
  height: 'height',
  artaSubtype: 'objectType',
  unitOfMeasurement: 'measurementUnit',
  isFragile: 'isFragile'
};

const calculateObjectConfidenceLevels = (shippingReviewData) => {
  const confidenceLevels = {};
  const argumentObjects = shippingReviewData?.arguments?.objects || [];
  const baseConfidence = shippingReviewData?.objectsExtraction?.confidence || {};

  argumentObjects.forEach((argumentObj) => {
    const objectId = argumentObj.id;
    confidenceLevels[objectId] = {};

    // workaround because the keys don't match in the two objects
    fieldsToCompareForConfidenceLevel.forEach((field) => {
      const fieldConfidence = baseConfidence[objectId]?.[confidenceProperties[field]];
      confidenceLevels[objectId][field] = fieldConfidence !== undefined ? fieldConfidence : 0;
    });
  });
  return confidenceLevels;
};

export const getShippingReviewData = (catalogID, houseID) => async (dispatch) => {
  dispatch({
    type: IS_SHIPPING_REVIEW_DATA_LOADING,
    payload: true,
  });
  try {
    await shippingApi.postShippingReview(catalogID);
    const NO_IMAGE = `${awsServer}/static/NoImage_140x140.png`;
    const response = await axios.get(`/cps/app/catalog/${catalogID}/summary`);
    const catalogRef = response.data.data.sessionDefinition.catalogRef;
    const sessionTitle = response.data.data.sessionDefinition.title;

    const shippingRes = await shippingApi.fetchReviews(catalogRef, houseID);
    const imageUrlsRes = await axios.get(`/cps/app/catalog/${catalogID}/house/${houseID}/primary-imageurls`);

    const deletedLots = response.data.data.deletedlots || [];
    const lotData = response.data.data.lots;
    const lotCount = response.data.data.lotcount;
    const shippingData = shippingRes?.data?.reviews;
    const ImgUrlsObject = imageUrlsRes.data.data;
    const imageUrlKeys = Object.keys(imageUrlsRes.data.data);
    let readyToConfirmCount = 0;
    let needsReviewCount = 0;
    let inProgressCount = 0;

    const nonDeletedLotsWithShippingData = lotData.reduce((acc, lot, index) => {
      // skip deleted lots
      if (deletedLots.includes(lot.lotNumber)) return acc;

      const lotShippingReviewID = lot.shippingReviewID;
      const shippingDataOfLot = shippingData.find((ship) => ship.id === lotShippingReviewID);
      if (shippingDataOfLot) {
        // append review data (with isModified and confidenceLevels) to the lot data
        lot.shippingReviewData = shippingDataOfLot;
        lot.shippingReviewData.isLotModified = isLotManuallyModified(
          lot.shippingReviewData.objectsExtraction.objects,
          lot.shippingReviewData.arguments.objects
        );
        lot.shippingReviewData.confidenceLevels = calculateObjectConfidenceLevels(lot.shippingReviewData);
        if (lot.shippingReviewData.status === 'awaiting_approval') {
          if (shippingDataOfLot?.objectsExtraction?.confidenceAll >= 0.8
            || lot.shippingReviewData?.isLotModified) {
            readyToConfirmCount += 1;
          } else {
            needsReviewCount += 1;
          }
        }
        if (lot.shippingReviewData.status === 'failed') {
          needsReviewCount += 1;
        }
        if (lot.shippingReviewData.status === 'pending') {
          inProgressCount += 1;
        }
      }
      const imageUrl = imageUrlKeys.find((imageLotNumber) => imageLotNumber === lot.lotNumber);
      if (imageUrl) {
        lotData[index].imageUrl = ImgUrlsObject[imageUrl];
      } else {
        lotData[index].imageUrl = NO_IMAGE;
      }
      acc.push(lot);
      return acc;
    }, []);

    dispatch({
      type: GET_SHIPPING_REVIEW_DATA_SUCCESS,
      payload: nonDeletedLotsWithShippingData,
    });
    dispatch({
      type: SET_SHIPPING_ITEM_COUNTS,
      payload: {
        shippingItemCountAll: lotCount,
        readyToConfirmCount,
        needsReviewCount,
        inProgressCount,
      },
    });
    dispatch({
      type: SET_SESSION_TITLE,
      payload: sessionTitle,
    });
  } catch (error) {
    console.log('error', error);
  } finally {
    dispatch({
      type: IS_SHIPPING_REVIEW_DATA_LOADING,
      payload: false,
    });
  }
};
export const updateReviewStatus = (review) => (dispatch, getState) => {
  let readyToConfirmCountChange = 0;
  let needsReviewCountChange = 0;
  let inProgressCountChange = 0;
  const confidenceAll = review.objectsExtraction?.confidenceAll;

  const updatedLotsWithShippingData = getState().shippingReview.lotsWithShippingData.map((lot) => {
    if (lot.shippingReviewID === review.id) {
      const transition = `${lot.shippingReviewData.status}->${review.status}`;
      switch (transition) {
        case 'pending->awaiting_approval':
          if (confidenceAll >= 0.8) {
            readyToConfirmCountChange += 1;
          }
          if (confidenceAll < 0.8) {
            needsReviewCountChange += 1;
          }
          inProgressCountChange -= 1;
          break;
        case 'pending->failed':
          needsReviewCountChange += 1;
          inProgressCountChange -= 1;
          break;
        case 'awaiting_approval->pending':
          if (confidenceAll >= 0.8) {
            readyToConfirmCountChange -= 1;
          }
          if (confidenceAll < 0.8) {
            needsReviewCountChange -= 1;
          }
          inProgressCountChange += 1;
          break;
        case 'failed->pending':
          needsReviewCountChange -= 1;
          inProgressCountChange += 1;
          break;
        case 'awaiting_approval->completed':
          if (confidenceAll >= 0.8) {
            readyToConfirmCountChange -= 1;
          }
          if (confidenceAll < 0.8) {
            needsReviewCountChange -= 1;
          }
          break;
        case 'pending->completed':
          inProgressCountChange -= 1;
          break;
        default:
          break;
      }
      // Calculate confidence levels for the updated review data
      const reviewWithConfidence = {
        ...review,
        confidenceLevels: review?.arguments?.objects && review?.objectsExtraction?.objects
          ? calculateObjectConfidenceLevels(review)
          : {},
        };
      return {
        ...lot,
        shippingReviewData: reviewWithConfidence
      };
    }
    return lot;
  });
  dispatch({
    type: UPDATE_SHIPPING_REVIEW_DATA_SUCCESS,
    payload: {
      lotsWithShippingData: updatedLotsWithShippingData,
      readyToConfirmCountChange,
      needsReviewCountChange,
      inProgressCountChange
    }
  });
};

export const updateShippingReviewData = (reviewID, houseID, payload) => async (dispatch) => {
  try {
    const response = await shippingApi.putShippingReview(reviewID, houseID, payload);
    let updatedReview = response.data.review;
    updatedReview.isLotModified = true;
    dispatch(updateReviewStatus(updatedReview));
  } catch (error) {
    console.error('Error updating shipping review data:', error);
  }
};

export const getArtaSubtypes = (abortSignal) => async (dispatch) => {
  try {
    const data = await shippingApi.fetchArtaSubtypes(abortSignal);
    const artaSubtypesMap = data?.reduce((acc, item) => {
      item.subtypes.forEach(subtype => {
        acc[subtype.id] = subtype;
      });
      return acc;
    }, {});

    const orderedListOfArtaSubtypes = data?.reduce((acc, item) => acc.concat(item.subtypes), []).sort((a, b) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    });

    dispatch({
      type: GET_ARTA_SUBTYPES_SUCCESS,
      payload: {
        artaSubtypeMap: artaSubtypesMap,
        artaSubtypeList: orderedListOfArtaSubtypes
      },
    });
  } catch (err) {
    console.error('Error fetching Arta subtypes:', err);
  }
};
